<template>
  <div class="ledger-page pb-5 pb-md-2">
    <b-card class="rounded-lg">
      <div class="d-flex justify-content-between align-items-start flex-wrap mb-1">
        <h4 class="mb-50">
          {{ $t('ledger_inv_title') }}
        </h4>
        <b-button
          v-if="canCreate && !hideNewInvoice"
          variant="primary"
          size="sm"
          class="rounded-pill"
          :to="{ name: 'ledger-invoices-new' }"
        >
          {{ $t('ledger_inv_new') }}
        </b-button>
      </div>

      <b-row>
        <b-col
          cols="12"
          md="3"
        >
          <b-form-input
            v-model="filter.keyword"
            :placeholder="$t('ledger_search')"
            @keyup.enter="reload"
          />
        </b-col>
        <b-col
          cols="6"
          md="2"
        >
          <b-form-select
            v-model="filter.status"
            :options="statusOptions"
            @change="reload"
          />
        </b-col>
        <b-col
          cols="6"
          md="2"
        >
          <b-form-input
            v-model="filter.from"
            type="date"
            @change="reload"
          />
        </b-col>
        <b-col
          cols="6"
          md="2"
        >
          <b-form-input
            v-model="filter.to"
            type="date"
            @change="reload"
          />
        </b-col>
        <b-col
          cols="6"
          md="3"
          class="d-flex align-items-center"
        >
          <b-form-checkbox
            v-model="filter.overdue"
            @change="reload"
          >
            {{ $t('ledger_inv_overdue_only') }}
          </b-form-checkbox>
        </b-col>
      </b-row>

      <div
        v-if="loading"
        class="text-center py-3"
      >
        <b-spinner variant="primary" />
      </div>
      <div
        v-else-if="!rows.length"
        class="text-center text-muted py-4"
      >
        {{ $t('ledger_inv_empty') }}
      </div>
      <div v-else>
        <div class="d-none d-md-block">
          <b-table
            :items="rows"
            :fields="fields"
            small
            hover
            responsive
            class="ledger-dense-table"
          >
            <template #cell(invoice_number)="data">
              <b-link :to="showRoute(data.item)">
                {{ data.item.invoice_number }}
              </b-link>
            </template>
            <template #cell(customer)="data">
              {{ customerName(data.item) }}
            </template>
            <template #cell(project)="data">
              {{ projectLabel(data.item) }}
            </template>
            <template #cell(gl_activity)="data">
              <b-badge variant="light-primary">
                {{ activityLabel(data.item.gl_activity) }}
              </b-badge>
            </template>
            <template #cell(total_amount)="data">
              <span class="ledger-num">{{ money(data.item.total_amount) }}</span>
            </template>
            <template #cell(balance_due)="data">
              <span class="ledger-num">{{ money(data.item.balance_due) }}</span>
            </template>
            <template #cell(status)="data">
              <b-badge :variant="statusVariant(data.item.status)">
                {{ enumLabel(data.item.status, $i18n.locale) }}
              </b-badge>
              <b-badge
                v-if="data.item.is_overdue"
                variant="danger"
                class="ml-50"
              >
                {{ $t('ledger_inv_overdue') }}
              </b-badge>
            </template>
          </b-table>
        </div>
        <div class="d-md-none">
          <article
            v-for="item in rows"
            :key="item.id"
            class="ledger-inv-card"
            @click="$router.push(showRoute(item))"
          >
            <div class="d-flex justify-content-between">
              <strong>{{ item.invoice_number }}</strong>
              <b-badge :variant="statusVariant(item.status)">
                {{ enumLabel(item.status, $i18n.locale) }}
              </b-badge>
            </div>
            <div>{{ customerName(item) }}</div>
            <div class="ledger-num">
              {{ money(item.balance_due) }}
            </div>
            <b-badge
              v-if="item.is_overdue"
              variant="danger"
            >
              {{ $t('ledger_inv_overdue') }}
            </b-badge>
          </article>
        </div>
        <b-pagination
          v-if="meta.total"
          v-model="filter.page"
          class="mt-1"
          align="center"
          :total-rows="meta.total"
          :per-page="meta.per_page || 15"
          @change="onPage"
        />
      </div>
    </b-card>
    <div
      v-if="rows.length"
      class="ledger-inv-sticky d-md-none"
    >
      <span>{{ $t('ledger_inv_balance') }}</span>
      <strong class="ledger-num">{{ money(balanceSum) }}</strong>
    </div>
  </div>
</template>

<script>
import {
  BCard, BRow, BCol, BFormInput, BFormSelect, BFormCheckbox, BButton, BTable, BBadge, BLink, BSpinner, BPagination,
} from 'bootstrap-vue'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import { getInvoices } from '@/api/accounting/invoices'
import { getProjectCost } from '@/api/accounting/projectCost'
import Projects from '@/api/projects/projects'
import {
  listFrom, oneFrom, enumValue, enumLabel, formatLedgerMoney, roundMoney, apiErrorMessage,
} from '@/utils/ledger'

const projectsApi = new Projects()

const STATUS_VARIANT = {
  draft: 'secondary',
  issued: 'info',
  partially_paid: 'warning',
  paid: 'success',
  cancelled: 'dark',
}

export default {
  name: 'InvoiceList',
  components: {
    BCard, BRow, BCol, BFormInput, BFormSelect, BFormCheckbox, BButton, BTable, BBadge, BLink, BSpinner, BPagination,
  },
  data() {
    return {
      loading: false,
      hideNewInvoice: false,
      rows: [],
      meta: {},
      filter: {
        keyword: '',
        status: this.$route.query.status || '',
        from: '',
        to: '',
        overdue: false,
        project_id: this.$route.query.project_id || '',
        customer_id: this.$route.query.customer_id || '',
        page: 1,
        per_page: 15,
      },
    }
  },
  computed: {
    canCreate() {
      return this.hasPermission('create invoices')
    },
    statusOptions() {
      return [
        { value: '', text: this.$t('ledger_assets_all_statuses') },
        { value: 'draft', text: this.$t('ledger_inv_status_draft') },
        { value: 'issued', text: this.$t('ledger_inv_status_issued') },
        { value: 'partially_paid', text: this.$t('ledger_inv_status_partial') },
        { value: 'paid', text: this.$t('ledger_inv_status_paid') },
        { value: 'cancelled', text: this.$t('ledger_inv_status_cancelled') },
      ]
    },
    fields() {
      return [
        { key: 'invoice_number', label: this.$t('ledger_inv_number') },
        { key: 'invoice_date', label: this.$t('ledger_date') },
        { key: 'customer', label: this.$t('ledger_inv_customer') },
        { key: 'project', label: this.$t('ledger_payroll_project') },
        { key: 'gl_activity', label: this.$t('ledger_payroll_activity') },
        { key: 'total_amount', label: this.$t('ledger_inv_total') },
        { key: 'balance_due', label: this.$t('ledger_inv_balance') },
        { key: 'status', label: this.$t('ledger_status') },
      ]
    },
    balanceSum() {
      return this.rows.reduce((sum, row) => roundMoney(sum + (Number(row.balance_due) || 0)), 0)
    },
  },
  mounted() {
    this.load()
    this.loadProjectGate()
  },
  methods: {
    enumLabel,
    money(value) {
      return formatLedgerMoney(value || 0, this.$i18n.locale)
    },
    statusVariant(status) {
      return STATUS_VARIANT[enumValue(status)] || 'secondary'
    },
    activityLabel(value) {
      const key = enumValue(value)
      return key ? this.$t(`ledger_inv_activity_${key}`) : '—'
    },
    customerName(row) {
      return (row.customer && row.customer.name) || '—'
    },
    projectLabel(row) {
      const project = row.project
      if (!project) return '—'
      return project.project_number ? `${project.project_number}` : (project.name || '—')
    },
    showRoute(row) {
      return { name: 'ledger-invoices-view', params: { id: row.id } }
    },
    reload() {
      this.filter.page = 1
      this.load()
    },
    onPage(page) {
      this.filter.page = page
      this.load()
    },
    inferActivity(project) {
      if (!project) return null
      const direct = enumValue(project.gl_activity)
      if (direct === 'survey' || direct === 'licensing' || direct === 'contracting' || direct === 'maps') return direct
      const service = project.service_type || project.serviceType || {}
      const activity = enumValue(service.effective_gl_activity) || enumValue(service.activity) || enumValue(service.gl_activity)
      if (activity === 'survey' || activity === 'licensing' || activity === 'contracting' || activity === 'maps') return activity
      return null
    },
    async loadProjectGate() {
      const id = this.filter.project_id
      if (!id) {
        this.hideNewInvoice = false
        return
      }
      try {
        const [costRes, projectRes] = await Promise.all([
          getProjectCost(id).catch(() => null),
          projectsApi.getProject(id).catch(() => null),
        ])
        const cost = costRes ? oneFrom(costRes) : null
        const project = projectRes ? oneFrom(projectRes) : null
        const activity = this.inferActivity(project) || enumValue(cost && cost.gl_activity)
        this.hideNewInvoice = Boolean(cost && cost.contract) || activity === 'contracting' || activity === 'maps'
      } catch (error) {
        this.hideNewInvoice = false
      }
    },
    async load() {
      this.loading = true
      try {
        const params = { page: this.filter.page, per_page: this.filter.per_page }
        if (this.filter.keyword) params.keyword = this.filter.keyword.trim()
        if (this.filter.status) params.status = this.filter.status
        if (this.filter.from) params.from = this.filter.from
        if (this.filter.to) params.to = this.filter.to
        if (this.filter.overdue) params.overdue = 1
        if (this.filter.project_id) params.project_id = this.filter.project_id
        if (this.filter.customer_id) params.customer_id = this.filter.customer_id
        const res = await getInvoices(params)
        this.rows = listFrom(res)
        this.meta = res.meta || {}
      } catch (error) {
        this.$toast({
          component: ToastificationContent,
          props: {
            title: apiErrorMessage(error, this.$t('ledger_error')),
            icon: 'AlertCircleIcon',
            variant: 'danger',
          },
        })
      } finally {
        this.loading = false
      }
    },
  },
}
</script>

<style scoped>
.ledger-num { font-variant-numeric: tabular-nums; white-space: nowrap; }
.ledger-inv-card { border-bottom: 1px solid #eee; padding: 0.85rem 0.15rem; cursor: pointer; }
.ledger-inv-sticky {
  position: fixed; bottom: 0; inset-inline: 0; z-index: 20; display: flex;
  justify-content: space-between; background: #fff; border-top: 1px solid #e6e6e6;
  padding: 0.75rem 1rem calc(0.75rem + env(safe-area-inset-bottom));
}
</style>
